/**
 * Контактные лица организации (company-profile-and-contacts): список + CRUD. Контакты ведутся
 * отдельно от сотрудников. Бейджи: «вы» (контакт текущего пользователя) и «уведомления»
 * (флаг receivesNotifications). Используется на клиентской и командной сторонах (через scope api).
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Plus } from 'lucide-react'
import { type CompanyScopeApi, type Contact, type ContactInput } from '@/lib/api'
import { getErrorMessage, fieldError } from '@/lib/errors'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { ListItem } from '@/design-system/components/data/list-item'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Switch } from '@/design-system/components/primitives/switch'
import { Alert } from '@/design-system/components/feedback/alert'
import { normalizePersonName, normalizePositionName } from '@/lib/name-case'

const EMPTY: ContactInput = {
  fullName: '',
  position: '',
  phone: '',
  email: '',
  receivesNotifications: false,
  maxContact: '',
}

function ContactForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: ContactInput
  submitLabel: string
  onSubmit: (input: ContactInput) => Promise<void>
  onCancel: () => void
}) {
  const [form, setForm] = useState<ContactInput>(initial)
  const [error, setError] = useState<unknown>(null)
  const [submitting, setSubmitting] = useState(false)

  function set<K extends keyof ContactInput>(key: K, value: ContactInput[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await onSubmit({ ...form, fullName: form.fullName.trim() })
    } catch (err) {
      setError(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate className="rounded-8 border border-line p-4">
      {error ? (
        <Alert variant="danger" className="mb-4">
          {getErrorMessage(error, 'Не удалось сохранить контакт')}
        </Alert>
      ) : null}
      <Grid cols={2}>
        <Field required label="ФИО" className="my-0" error={fieldError(error, 'fullName')}>
          {(props) => (
            <Input
              {...props}
              value={form.fullName}
              onChange={(e) => set('fullName', e.target.value)}
              // Регистр — на уходе с поля (names-and-positions-normalization).
              onBlur={() => set('fullName', normalizePersonName(form.fullName))}
              spellCheck={false}
              required
            />
          )}
        </Field>
        <Field label="Должность" className="my-0" error={fieldError(error, 'position')}>
          {(props) => (
            <Input
              {...props}
              value={form.position}
              onChange={(e) => set('position', e.target.value)}
              onBlur={() => set('position', normalizePositionName(form.position))}
              spellCheck
              lang="ru"
            />
          )}
        </Field>
        <Field label="Телефон" className="my-0" error={fieldError(error, 'phone')}>
          {(props) => (
            <Input {...props} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          )}
        </Field>
        <Field label="Email" className="my-0" error={fieldError(error, 'email')}>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
          )}
        </Field>
        <Field label="Мессенджер «Макс»" className="my-0" error={fieldError(error, 'maxContact')}>
          {(props) => (
            <Input
              {...props}
              value={form.maxContact ?? ''}
              onChange={(e) => set('maxContact', e.target.value)}
            />
          )}
        </Field>
        <div className="flex items-center justify-between gap-3 py-2">
          <span className="text-13 text-txt-secondary">Получает уведомления</span>
          <Switch
            checked={form.receivesNotifications}
            onCheckedChange={(v) => set('receivesNotifications', v)}
            aria-label="Получает уведомления"
          />
        </div>
      </Grid>
      <div className="mt-4 flex gap-2">
        <Button type="submit" disabled={submitting || form.fullName.trim() === ''}>
          {submitting ? 'Сохранение…' : submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </form>
  )
}

export function ContactsManager({
  api,
  currentUserEmail,
}: {
  api: CompanyScopeApi
  currentUserEmail?: string
}) {
  const [items, setItems] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | 'new' | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    api
      .listContacts(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setItems(res.items)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить контакты'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [api])

  async function create(input: ContactInput) {
    const created = await api.createContact(input)
    setItems((prev) => [created, ...prev])
    setEditing(null)
  }

  async function update(id: string, input: ContactInput) {
    const updated = await api.updateContact(id, input)
    setItems((prev) => prev.map((c) => (c.id === id ? updated : c)))
    setEditing(null)
  }

  async function remove(id: string) {
    setActionError(null)
    try {
      await api.deleteContact(id)
      setItems((prev) => prev.filter((c) => c.id !== id))
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось удалить контакт'))
    }
  }

  function isYou(c: Contact): boolean {
    return Boolean(currentUserEmail && c.email && c.email.toLowerCase() === currentUserEmail.toLowerCase())
  }

  return (
    <Card header="Контактные лица">
      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      {actionError ? (
        <Alert variant="danger" className="mb-4">
          {actionError}
        </Alert>
      ) : null}

      {loading ? (
        <p className="py-6 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <div>
          {items.map((c) =>
            editing === c.id ? (
              <ContactForm
                key={c.id}
                initial={c}
                submitLabel="Сохранить"
                onSubmit={(input) => update(c.id, input)}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <ListItem
                key={c.id}
                name={
                  <span className="flex flex-wrap items-center gap-2">
                    {c.fullName}
                    {isYou(c) ? <Badge variant="blue">вы</Badge> : null}
                    {c.receivesNotifications ? <Badge variant="green">уведомления</Badge> : null}
                  </span>
                }
                meta={[c.position, c.email, c.phone].filter(Boolean).join(' · ')}
                action={
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setEditing(c.id)}>
                      Изменить
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => void remove(c.id)}>
                      Удалить
                    </Button>
                  </div>
                }
              />
            ),
          )}
          {items.length === 0 ? (
            <p className="py-4 text-txt-secondary">Контактных лиц пока нет.</p>
          ) : null}
        </div>
      )}

      {editing === 'new' ? (
        <div className="mt-3">
          <ContactForm
            initial={EMPTY}
            submitLabel="Добавить"
            onSubmit={create}
            onCancel={() => setEditing(null)}
          />
        </div>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          className="mt-3"
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
          onClick={() => setEditing('new')}
        >
          Добавить контакт
        </Button>
      )}
    </Card>
  )
}
